<template>
  <Upload v-bind="getBindValue" v-model:file-list="fileList" :class="cardClass">
    <template v-for="item in Object.keys($slots)" #[item]="data">
      <slot :name="item" v-bind="data || {}"></slot>
    </template>

    <slot>
      <MButton
        v-if="listType === 'text'"
        v-bind="{ ...btnProps, disabled: getBindValue.disabled || btnProps?.disabled }"
        :loading="uploading"
      >
        <slot name="btnText">{{ t(label) }} </slot>
      </MButton>
      <div v-else class="flex flex-col items-center justify-center">
        <PlusOutlined />
        <span class="mt-8px">{{ t(label) }}</span>
      </div>
    </slot>
  </Upload>
</template>

<script setup lang="ts">
  import { PlusOutlined } from '@ant-design/icons-vue';
  import { Upload, UploadChangeParam, UploadFile, UploadProps } from 'ant-design-vue';
  import { isArray, isBoolean, isObject, isString } from 'lodash-es';
  import { computed, ref, unref, watch } from 'vue';
  import { useAttrs } from '@mts-fe/hooks';
  import { cloneDeep, message, omit } from '@mts-fe/utils';
  import { MButton } from '@mts/components/Button';
  import { createImgPreview } from '@mts/components/Preview';
  import { t } from '@mts/hooks/web/useI18n';
  import { useMessage } from '@mts/hooks/web/useMessage';
  import { getRequestHeaders } from '@mts/utils/auth';
  import { globSetting } from '@mts/utils/env';
  import type { MUploadProps } from '..';

  defineOptions({
    name: 'MUpload',
    inheritAttrs: false,
  });

  const props = withDefaults(defineProps<MUploadProps>(), {
    size: 'large',
    uploadType: 'other',
    maxCount: 1,
    maxSize: 100,
    listType: 'text',
    showUploadList: false,
    openFileDialogOnClick: true,
    label: 'component.upload.choose',
    deleteConfirm: true,
    allowClear: false,
  });
  const attrs = useAttrs();
  const cardClass = computed(() => {
    if (props.listType === 'picture-card') {
      return ['mupload-size-' + props.size];
    }
    return [''];
  });

  /**
   * 上传到服务器的文件夹地址，用于后面区分图片和文件的路径
   * img 上传图片
   * excel 导入Excel
   * temp 临时文件
   * other 其他文件
   */
  const ACCEPT_MAP = {
    img: { bucket: 'pic-file-bucket', accepts: ['png', 'jpg', 'jpeg', 'svg'] },
    excel: { bucket: 'temp-file-bucket', accepts: ['xls', 'xlsx'] },
    temp: { bucket: 'temp-file-bucket', accepts: [] },
    other: { bucket: 'other-file-bucket', accepts: [] },
  };
  const getStringAccept = computed(
    () =>
      props.accept ||
      ACCEPT_MAP[props.uploadType].accepts
        .map((i: string) => (i.indexOf('/') > 0 || i.startsWith('.') ? i : '.' + i))
        .join(','),
  );

  //判断文件类型
  const checkFileType = (file: File, accept: string) => {
    const types = accept
      .split(',')
      .map((i) => (i.startsWith('.') ? i.slice(1) : i))
      .join('|');
    return new RegExp('\\.(' + types + ')$', 'i').test(file.name);
  };

  const fileList = ref<UploadFile[]>([]);
  const allFileList = ref<UploadFile[]>([]);
  // 记录被校验拒绝的文件 uid，便于在 onChange 中将其从列表中移除
  const rejectedUids = ref<Set<string>>(new Set());
  const beforeUpload: MUploadProps['beforeUpload'] = (file, _fileList) => {
    if (props.beforeUpload) return props.beforeUpload(file, _fileList);
    allFileList.value = _fileList;
    let accept = getStringAccept.value;
    // 设置类型,则判断
    if (accept.length && !checkFileType(file, accept)) {
      message.error!(t('component.upload.acceptUpload', [accept]));
      rejectedUids.value.add((file as any).uid);
      return false;
    }
    // 设置最大值，则判断
    if (props.maxSize && file.size / 1024 / 1024 >= props.maxSize) {
      message.error!(t('component.upload.maxSizeMultiple', [props.maxSize]));
      // 超出文件可上传最大值，标记当前文件，待 onChange 中从文件列表中移除
      rejectedUids.value.add((file as any).uid);
      return false;
    }

    if (props.maxCount === 1 && fileList.value.length) {
      fileList.value = [];
    }
    // 最大数量
    if (props.maxCount && fileList.value.length >= props.maxCount) {
      message.error(t('component.upload.maxNumber', [props.maxCount]));
      rejectedUids.value.add((file as any).uid);
      return false;
    }

    return file;
  };

  const uploading = ref(false);
  const emit = defineEmits([
    'uploadSuccess',
    'uploading',
    'update:value',
    'change',
    'done',
    'allSuccess',
  ]);
  const onChange = ({ file }: UploadChangeParam) => {
    // 移除被校验拒绝的文件（超限大小、类型不符等）
    if (rejectedUids.value.size > 0) {
      fileList.value = fileList.value.filter((item) => !rejectedUids.value.has(item.uid));
      rejectedUids.value.clear();
    }
    if (file.status === 'uploading') {
      uploading.value = true;
      emit('uploading', file.name);
    }
    if (file.status === 'done') {
      uploading.value = false;
      emit('done', file.name);
      if (file.response?.error) {
        return message.error(file.response.message);
      }
      const fileUrl = file.response.data?.uploadKey;
      emit('uploadSuccess', { fileName: file.name, fileUrl, file });

      fileList.value = fileList.value.map((file) => ({
        ...file,
        name: file.name || file.response?.data?.fileName,
        url: file.url || file.response?.data?.uploadKey,
      }));
      changeValue();
    } else if (file.status === 'error') {
      uploading.value = false;
      message.error(t('component.upload.uploadError') + ' ' + (file.response?.message || ''));
    }
  };

  const isUploadChange = ref(false);
  const changeValue = (files = fileList.value) => {
    const list = files.map((item) => item.url);
    const value = props.maxCount > 1 ? list : list.length > 0 ? list[0] : '';
    // 如果手动上传，就不需要监听value的改变了
    isUploadChange.value = true;
    emit('update:value', value);
    emit('change', value);

    // 批量上传只需要上报一次
    if (list.filter((url) => !!url).length === allFileList.value.length) {
      const _files = files.map((file) => ({
        fileName: file.name || file.response?.data?.fileName,
        fileUrl: file.url || file.response?.data?.uploadKey,
      }));
      emit('allSuccess', { list, files: cloneDeep(_files) });
      allFileList.value = [];

      if (props.allowClear) {
        fileList.value = [];
      }
    }
  };

  const { createConfirm } = useMessage();

  watch(
    () => props.value,
    (v) => {
      // 如果手动上传，就不需要监听value的改变了
      if (isUploadChange.value === true) {
        return;
      }
      if (v) {
        let value: string[] = [];
        if (isArray(v)) {
          value = v;
        } else {
          value.push(v);
        }
        fileList.value = value
          .filter((item) => !!item)
          .map((item, i) => {
            if (item && isString(item)) {
              return {
                uid: -i + '',
                name: decodeURIComponent(item.substring(item.lastIndexOf('/') + 1)),
                status: 'done',
                url: item,
              };
            } else if (item && isObject(item)) {
              return item;
            } else {
              return;
            }
          }) as UploadFile[];
      }
    },
    { immediate: true, deep: true },
  );

  const getBindValue = computed(() => {
    const action = globSetting.apiUrl + (props.action || '/ims-system/system/file/upload');
    return {
      ...unref(attrs),
      ...omit(props, ['btnProps', 'uploadType', 'label', 'maxSize']),
      action,
      headers: getRequestHeaders({ url: action }),
      accept: getStringAccept.value,
      data: {
        bucketName: ACCEPT_MAP[props.uploadType].bucket,
        makeFileNameEndWithTimestamp: true,
        randomFileName: false,
        ...props.data,
      },
      showUploadList:
        isBoolean(props.showUploadList) && props.showUploadList
          ? { showDownloadIcon: true }
          : props.showUploadList,
      beforeUpload,
      onChange,
      onPreview: (file) => {
        if (checkFileType(file as unknown as File, ACCEPT_MAP.img.accepts.join(','))) {
          file.url && createImgPreview({ imageList: [file.url] });
        } else {
          file.url && window.open(file.url, '_blank');
        }
      },
      onRemove: (file) => {
        if (props.deleteConfirm) {
          return new Promise((resolve) => {
            createConfirm({
              title: t('common.reminder'),
              iconType: 'warning',
              content: t('common.deleteConfirm'),
              onOk: () => {
                console.log('---------ok');
                fileList.value = fileList.value.filter((item) => item.url !== file.url);
                changeValue();
                resolve(true);
              },
            });
          });
        } else {
          fileList.value = fileList.value.filter((item) => item.url !== file.url);
          changeValue();
        }
      },
    } as UploadProps;
  });

  const clear = () => {
    fileList.value = [];
    changeValue();
  };
  defineExpose({ clear });
</script>
<style lang="less" scoped>
  :deep(.ant-upload-disabled) {
    display: none !important;
  }

  @size: {
    small: 100px;
    middle: 150px;
    large: 200px;
  };

  each(@size,{
    .mupload-size-@{key} {
      :deep(.ant-upload-list-item-container){
        width: @value !important;
        height: @value !important;
      }

      :deep(.ant-upload){
        width: @value !important;
        height:@value !important;
      }

      :deep(.ant-btn.ant-btn-sm.ant-btn-icon-only){
        width: (@value/4) !important;
        height: (@value/4) !important;
      }

      :deep(span.anticon){
        font-size: (@value / 100 * 14) !important;
        width: (@value / 100 * 14) !important;
      }

      :deep(.anticon-eye){
          margin: 0 (@value / 100 * 4) !important;
      }
    }
  });
</style>
